import React, { useState, useEffect } from 'react';
import { Bell, MessageCircle, ChevronDown, User as UserIcon, LogOut, Inbox, Moon, Sun, Menu } from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';
import { User } from '../../types';
import { useNotifications } from '../../hooks/useNotifications';
import { useSocket } from '../../hooks/useSocket';
import { useDarkMode } from '../../contexts/DarkModeContext';
import { GlobalSearch, type SearchNavigateTarget } from '../common/GlobalSearch';
import Avatar from '../common/Avatar';
import { toast } from '../../utils/toast';


interface HeaderProps {
  currentUser: User;
  onSearch?: (searchTerm: string) => void;
  onNotificationClick?: () => void;
  onProfileClick?: () => void;
  onChatClick?: () => void;
  unreadMessagesCount?: number;
  // Opens the off-canvas sidebar below md
  onMenuClick?: () => void;
  // Admin-configured app name (GET /api/settings)
  appTitle?: string;
  // Global search result clicked — App switches view accordingly
  onSearchNavigate?: (target: SearchNavigateTarget) => void;
}

type DevopsAvailabilityStatus = 'available' | 'leave' | 'busy';

// UTC's calendar date lags IST by up to a day during the first ~5.5 hours of
// each IST day — using new Date().toISOString() for "today" here would let
// someone pick a date that's actually already passed locally. 'en-CA' happens
// to format as ISO (YYYY-MM-DD).
const todayISTDateString = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const AVAILABILITY_OPTIONS: { value: DevopsAvailabilityStatus; label: string; dot: string; chip: string }[] = [
  { value: 'available', label: 'Available', dot: 'bg-green-500', chip: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'busy', label: 'Busy', dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  { value: 'leave', label: 'On Leave', dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200' },
];

const Header: React.FC<HeaderProps> = ({
  currentUser,
  onNotificationClick,
  onProfileClick,
  onChatClick,
  unreadMessagesCount = 0,
  onMenuClick,
  appTitle,
  onSearchNavigate
}) => {
  const { keycloak } = useKeycloak();
  const [showNotifications, setShowNotifications] = useState(false);
  const { notifications, unreadCount, loading, refresh, markAsRead, markAllAsRead } = useNotifications(currentUser);
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { isConnected, joinRoom, leaveRoom, on, off } = useSocket();

  // DevOps self-reported availability — lets a requester's slot-booking
  // dropdown know who's actually free to take a deployment, instead of
  // assigning someone who's on leave or mid-incident on something else.
  const [availabilityStatus, setAvailabilityStatus] = useState<DevopsAvailabilityStatus>('available');
  const [showAvailabilityMenu, setShowAvailabilityMenu] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);

  // Future leave announced ahead of time (e.g. "I'm on leave tomorrow") —
  // separate from the live status above so setting this today doesn't also
  // mark today itself unavailable.
  const [scheduledLeave, setScheduledLeave] = useState<{ _id: string; fromDate: string; toDate: string; reason?: string }[]>([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [leaveFromDate, setLeaveFromDate] = useState('');
  const [leaveToDate, setLeaveToDate] = useState('');
  const [leaveReason, setLeaveReason] = useState('');
  const [savingLeave, setSavingLeave] = useState(false);

  // Admin view: set any DevOps engineer's status (e.g. someone is out sick and
  // couldn't flip their own). The server always allowed this; the UI never did.
  const [showTeamAvailability, setShowTeamAvailability] = useState(false);
  const [devopsTeam, setDevopsTeam] = useState<{ id: string; name: string; email: string; status: DevopsAvailabilityStatus }[]>([]);

  const loadTeamAvailability = async () => {
    try {
      const [usersRes, availRes] = await Promise.all([fetch('/api/users'), fetch('/api/devops-availability')]);
      const users = await usersRes.json();
      const avail: any[] = await availRes.json();
      const team = (Array.isArray(users) ? users : [])
        .filter((u: any) => (u.realmRoles || u.roles || []).some((r: string) => r.toLowerCase() === 'devops'))
        .map((u: any) => ({
          id: u.id,
          name: u.name || u.username,
          email: u.email || '',
          status: (avail.find(a => a.userId === u.id)?.status || 'available') as DevopsAvailabilityStatus
        }));
      setDevopsTeam(team);
    } catch (err) {
      console.error('Error loading team availability:', err);
    }
  };

  const setTeamMemberStatus = async (member: { id: string; name: string; email: string }, status: DevopsAvailabilityStatus) => {
    const previous = devopsTeam;
    setDevopsTeam(devopsTeam.map(m => m.id === member.id ? { ...m, status } : m));
    try {
      const res = await fetch(`/api/devops-availability/${member.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: member.name, email: member.email, status })
      });
      if (!res.ok) throw new Error('Failed to update');
    } catch (err) {
      console.error('Error updating team member availability:', err);
      setDevopsTeam(previous);
      toast('Failed to update that engineer\'s availability.');
    }
  };

  const refreshOwnAvailability = () => {
    if (currentUser.role !== 'devops') return;
    fetch(`/api/devops-availability`)
      .then(res => res.json())
      .then((records: any[]) => {
        const mine = Array.isArray(records) ? records.find(r => r.userId === currentUser.id) : null;
        if (mine?.status) setAvailabilityStatus(mine.status);
        setScheduledLeave(Array.isArray(mine?.scheduledLeave) ? mine.scheduledLeave : []);
      })
      .catch(err => console.error('Error fetching own availability:', err));
  };

  useEffect(() => {
    refreshOwnAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser.role, currentUser.id]);

  const handleSetAvailability = async (status: DevopsAvailabilityStatus) => {
    if (status === availabilityStatus) return;
    const previous = availabilityStatus;
    setAvailabilityStatus(status);
    setSavingAvailability(true);
    try {
      const res = await fetch(`/api/devops-availability/${currentUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: currentUser.name, email: currentUser.email, status })
      });
      if (!res.ok) throw new Error('Failed to update availability');
    } catch (err) {
      console.error('Error updating availability:', err);
      setAvailabilityStatus(previous);
      toast('Failed to update your availability. Please try again.');
    } finally {
      setSavingAvailability(false);
    }
  };

  const handleScheduleLeave = async () => {
    if (!leaveFromDate || !leaveToDate) {
      toast('Please pick both a from and to date.');
      return;
    }
    if (leaveToDate < leaveFromDate) {
      toast('The "to" date cannot be before the "from" date.');
      return;
    }
    setSavingLeave(true);
    try {
      const res = await fetch(`/api/devops-availability/${currentUser.id}/leave`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentUser.name,
          email: currentUser.email,
          fromDate: leaveFromDate,
          toDate: leaveToDate,
          reason: leaveReason
        })
      });
      if (!res.ok) throw new Error('Failed to schedule leave');
      const updated = await res.json();
      setScheduledLeave(Array.isArray(updated?.scheduledLeave) ? updated.scheduledLeave : []);
      setLeaveFromDate('');
      setLeaveToDate('');
      setLeaveReason('');
      setShowScheduleForm(false);
    } catch (err) {
      console.error('Error scheduling leave:', err);
      toast('Failed to schedule leave. Please try again.');
    } finally {
      setSavingLeave(false);
    }
  };

  const handleCancelScheduledLeave = async (leaveId: string) => {
    try {
      const res = await fetch(`/api/devops-availability/${currentUser.id}/leave/${leaveId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to cancel leave');
      const updated = await res.json();
      setScheduledLeave(Array.isArray(updated?.scheduledLeave) ? updated.scheduledLeave : []);
    } catch (err) {
      console.error('Error cancelling scheduled leave:', err);
      toast('Failed to cancel that leave entry. Please try again.');
    }
  };

  const currentAvailabilityOption = AVAILABILITY_OPTIONS.find(o => o.value === availabilityStatus) || AVAILABILITY_OPTIONS[0];

  // The backend already emits a 'notification' socket event to `user-${id}` the
  // moment a notification is created (server.cjs) — this hook existed but was
  // never mounted anywhere, so notifications only ever appeared after the next
  // 30s poll in useNotifications. Use the live event just to trigger an
  // immediate refresh, keeping useNotifications' REST-backed state as the single
  // source of truth instead of duplicating it.
  useEffect(() => {
    if (!isConnected || !currentUser?.id) return;
    joinRoom(`user-${currentUser.id}`);
    const handleLiveNotification = () => refresh();
    on('notification', handleLiveNotification);
    return () => {
      off('notification', handleLiveNotification);
      leaveRoom(`user-${currentUser.id}`);
    };
  }, [isConnected, currentUser?.id]);

  const handleLogout = () => {
    keycloak.logout({
      redirectUri: window.location.origin
    });
  };

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const handleProfileClick = () => {
    if (onProfileClick) {
      onProfileClick();
    } else {
      // Default behavior - show profile info
      toast(`👤 Profile Information\n\nName: ${currentUser.name}\nEmail: ${currentUser.email}\nRole: ${currentUser.role.toUpperCase()}`);
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-4">
      <div className="flex items-center justify-between gap-2 sm:gap-4">
        {onMenuClick && (
          <button
            type="button"
            onClick={onMenuClick}
            className="md:hidden p-2 -ml-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}
        {appTitle && (
          <span className="hidden lg:block text-base font-semibold text-gray-900 whitespace-nowrap truncate max-w-[16rem]" title={appTitle}>
            {appTitle}
          </span>
        )}
        <div className="flex items-center space-x-4 flex-1 min-w-0 max-w-2xl" data-tour="header-search">
          <GlobalSearch onNavigate={onSearchNavigate} />
        </div>

        <div className="flex items-center space-x-1 sm:space-x-4">
          {/* Dark Mode Toggle */}
          <button
            onClick={toggleDarkMode}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {isDarkMode ? (
              <Sun className="w-5 h-5" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>

          {/* Role Display (Read-only) - Removed role switcher as per user request */}
          <div className="hidden lg:block bg-blue-50 text-blue-700 px-3 py-1 rounded-lg border border-blue-200 text-sm font-medium">
            {currentUser.role === 'tl' ? 'Team Lead' :
             currentUser.role === 'pm' ? 'Program Manager' :
             currentUser.role === 'devops' ? 'DevOps Engineer' :
             currentUser.role === 'admin' ? 'Administrator' :
             currentUser.role === 'tester' ? 'Tester' :
             'Developer'}
          </div>

          {/* DevOps availability selector — controls whether this engineer
              shows up selectable in the slot-booking "assign DevOps" dropdown */}
          {currentUser.role === 'devops' && (
            <div className="relative">
              <button
                onClick={() => setShowAvailabilityMenu(!showAvailabilityMenu)}
                disabled={savingAvailability}
                className={`flex items-center gap-2 px-3 py-1 rounded-lg border text-sm font-medium transition-colors disabled:opacity-60 ${currentAvailabilityOption.chip}`}
                title="Set your availability for slot booking"
              >
                <span className={`w-2 h-2 rounded-full ${currentAvailabilityOption.dot}`}></span>
                {currentAvailabilityOption.label}
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {showAvailabilityMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => { setShowAvailabilityMenu(false); setShowScheduleForm(false); }} />
                  <div className="absolute left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                    <p className="px-3 pt-1 pb-1.5 text-xs font-semibold text-gray-400 uppercase">Right now</p>
                    {AVAILABILITY_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => handleSetAvailability(opt.value)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 ${opt.value === availabilityStatus ? 'font-semibold text-gray-900' : 'text-gray-700'}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${opt.dot}`}></span>
                        {opt.label}
                      </button>
                    ))}

                    <div className="border-t border-gray-100 mt-1 pt-2 px-3 pb-2">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-1.5">Planned leave</p>

                      {scheduledLeave.length > 0 && (
                        <div className="space-y-1 mb-2">
                          {scheduledLeave.map(entry => (
                            <div key={entry._id} className="flex items-center justify-between gap-2 bg-red-50 border border-red-100 rounded-md px-2 py-1">
                              <span className="text-xs text-red-700">
                                {entry.fromDate === entry.toDate ? entry.fromDate : `${entry.fromDate} → ${entry.toDate}`}
                                {entry.reason ? ` · ${entry.reason}` : ''}
                              </span>
                              <button
                                onClick={() => handleCancelScheduledLeave(entry._id)}
                                className="text-red-400 hover:text-red-600 text-xs font-bold shrink-0"
                                title="Cancel this leave"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {!showScheduleForm ? (
                        <button
                          onClick={() => setShowScheduleForm(true)}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          + Schedule future leave
                        </button>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex gap-1.5">
                            <input
                              type="date"
                              value={leaveFromDate}
                              onChange={(e) => setLeaveFromDate(e.target.value)}
                              min={todayISTDateString()}
                              className="w-1/2 px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                            <input
                              type="date"
                              value={leaveToDate}
                              onChange={(e) => setLeaveToDate(e.target.value)}
                              min={leaveFromDate || todayISTDateString()}
                              className="w-1/2 px-2 py-1 border border-gray-300 rounded text-xs"
                            />
                          </div>
                          <input
                            type="text"
                            value={leaveReason}
                            onChange={(e) => setLeaveReason(e.target.value)}
                            placeholder="Reason (optional)"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => setShowScheduleForm(false)}
                              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleScheduleLeave}
                              disabled={savingLeave}
                              className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                            >
                              {savingLeave ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {currentUser.role === 'admin' && (
            <div className="relative">
              <button
                onClick={() => { setShowTeamAvailability(!showTeamAvailability); if (!showTeamAvailability) loadTeamAvailability(); }}
                className="flex items-center gap-2 px-3 py-1 rounded-lg border border-gray-200 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                title="Set DevOps engineers' availability"
              >
                DevOps availability
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {showTeamAvailability && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowTeamAvailability(false)} />
                  <div className="absolute left-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-2">
                    <p className="px-3 pb-1.5 text-xs font-semibold text-gray-400 uppercase">DevOps team — today</p>
                    {devopsTeam.length === 0 && <p className="px-3 py-2 text-sm text-gray-500">No DevOps engineers found.</p>}
                    {devopsTeam.map(member => (
                      <div key={member.id} className="flex items-center justify-between gap-2 px-3 py-1.5">
                        <span className="text-sm text-gray-800 truncate">{member.name}</span>
                        <select
                          value={member.status}
                          onChange={(e) => setTeamMemberStatus(member, e.target.value as DevopsAvailabilityStatus)}
                          className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white"
                        >
                          {AVAILABILITY_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {onChatClick && (
            <button
              onClick={onChatClick}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Open chat"
            >
              <MessageCircle className="w-5 h-5" />
              {unreadMessagesCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                </span>
              )}
            </button>
          )}

          <div className="relative" data-tour="header-notifications">
            <button
              onClick={handleNotificationClick}
              className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="View notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-[calc(100vw-1.5rem)] max-w-96 sm:w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[600px] flex flex-col">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white rounded-t-lg">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          markAllAsRead();
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        title="Mark all as read"
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        refresh();
                      }}
                      className="text-gray-400 hover:text-gray-600"
                      title="Refresh"
                    >
                      🔄
                    </button>
                  </div>
                </div>
                
                <div className="overflow-y-auto flex-1">
                  {loading ? (
                    <div className="p-8 text-center text-gray-500">
                      <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                      Loading...
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="flex justify-center mb-4">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <Inbox className="w-8 h-8 text-gray-400" />
                        </div>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h4>
                      <p className="text-sm text-gray-500 mb-4">No new notifications at this time.</p>
                      <div className="space-y-2 text-xs text-gray-400">
                        <p>💡 You'll receive notifications for:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Deployment approvals & rejections</li>
                          <li>New deployment requests (TL/PM)</li>
                          <li>Testing assignments</li>
                          <li>System updates & announcements</li>
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-100">
                      {notifications.map((notif) => (
                        <div
                          key={notif.id}
                          onClick={() => {
                            markAsRead(notif.id);
                            window.location.hash = `#deployment-${notif.deploymentId}`;
                            setShowNotifications(false);
                          }}
                          className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                            !notif.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                              notif.color === 'red' ? 'bg-red-500' :
                              notif.color === 'green' ? 'bg-green-500' :
                              notif.color === 'yellow' ? 'bg-yellow-500' :
                              'bg-blue-500'
                            }`}></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={`text-sm font-medium ${!notif.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                  {notif.title}
                                </p>
                                {!notif.read && (
                                  <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></span>
                                )}
                              </div>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {notif.message}
                              </p>
                              <div className="flex items-center gap-3 mt-2">
                                <span className="text-xs text-gray-400">
                                  {new Date(notif.timestamp).toLocaleDateString('en-IN', {
                                    day: '2-digit',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  notif.priority === 'high' ? 'bg-red-100 text-red-700' :
                                  notif.priority === 'low' ? 'bg-green-100 text-green-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {notif.priority.toUpperCase()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-3 border-t border-gray-100 bg-gray-50 rounded-b-lg sticky bottom-0">
                  <button 
                    onClick={() => setShowNotifications(false)}
                    className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User Profile Dropdown */}
          <div className="relative group" data-tour="header-profile">
            <button className="flex items-center space-x-3 bg-gray-50 hover:bg-gray-100 px-2 sm:px-3 py-2 rounded-lg shadow-sm transition-colors">
              <Avatar name={currentUser.name} userId={currentUser.id} profilePicture={currentUser.profilePicture} size="sm" />
              <div className="hidden md:block text-sm text-left">
                <p className="font-medium text-gray-900">{currentUser.name}</p>
                <p className="text-gray-500 capitalize text-xs">{currentUser.role}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>

            {/* Dropdown Menu */}
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="p-3 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <Avatar name={currentUser.name} userId={currentUser.id} profilePicture={currentUser.profilePicture} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{currentUser.name}</p>
                    <p className="text-xs text-gray-500 truncate">{currentUser.email}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-2">
                <button 
                  onClick={handleProfileClick}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                >
                  <UserIcon className="w-4 h-4" />
                  <span>My Profile</span>
                </button>
                
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors mt-1"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;